import * as Dialog from '@radix-ui/react-dialog';
import { Button } from 'components/ui/button';
import { X } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { ImageGenService } from '../../services/image-gen.api';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { saveFile, savePromptInfo, setFileName } from 'store/slices/imageGen';
import toast from 'react-hot-toast';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useCallback } from 'react';

interface RegenerateModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function RegenerateModal({ isOpen, onOpenChange }: RegenerateModalProps) {
  const dispatch = useDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { input, prompt_type } = useSelector((state: RootState) => state.imageGen);

  const { mutate, isPending } = useMutation({
    mutationKey: ['regenerate-image'],
    mutationFn: ImageGenService.generateImageFromText,
    onSuccess: (data) => {
      dispatch(savePromptInfo({
        tempImagePath: data.data.image_url,
        prompt_type,
        input,
      }));
      
      dispatch(saveFile({ fileSaved: false }));
      dispatch(setFileName({ fileName: 'Untitled 1' }));
      
      onOpenChange(false);
      
      toast.success('Image regenerated successfully!', {
        position: 'bottom-center',
      });
    },
    onError: () => {
      toast.error('Failed to regenerate image. Please try again.', {
        position: 'bottom-center',
      });
    },
  });

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) return;
    return await executeRecaptcha('KaiRecaptcha0911');
  }, [executeRecaptcha]);

  const handleRegenerate = async () => {
    try {
      const token = await handleReCaptchaVerify();
      mutate({
        input,
        prompt_type,
        token,
      });
    } catch (error) {
      toast.error('Failed to verify reCAPTCHA. Please try again.', {
        position: 'bottom-center',
      });
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed text-text-primary left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-background p-6 shadow-lg w-[400px] max-w-[calc(100%-32px)]">
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-start">
              <Dialog.Title className="text-2xl font-bold">
                Regenerate Image
              </Dialog.Title>
              <Dialog.Close asChild>
                <button className="rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </Dialog.Close>
            </div>
            
            <p>
              Are you sure you want to regenerate this image? This will create a new image with the same prompt and style.
            </p>

            <div className="flex justify-end gap-2 mt-2">
              <Button
                onClick={() => onOpenChange(false)}
                variant="customSecondary"
                size="sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRegenerate}
                variant="customPrimary"
                size="sm"
                disabled={isPending}
              >
                {isPending ? 'Regenerating...' : 'Regenerate'}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}